import { memo } from 'react';
import PropTypes from 'prop-types';
import { ChevronRight } from 'lucide-react';

/** @type {Record<string, string>} */
const VARIANT_STYLES = {
  neutral: 'border-edge bg-surface-muted/40 hover:bg-surface-muted/70',
  blue: 'border-blue-500/25 bg-blue-500/5 hover:bg-blue-500/10',
  purple: 'border-purple-500/25 bg-purple-500/5 hover:bg-purple-500/10',
  green: 'border-emerald-500/25 bg-emerald-500/5 hover:bg-emerald-500/10',
  amber: 'border-amber-500/25 bg-amber-500/5 hover:bg-amber-500/10',
  red: 'border-red-500/25 bg-red-500/5 hover:bg-red-500/10',
};

/** @type {Record<string, string>} */
const LABEL_ALIGN_CLASSES = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
};

const ICON_WRAPPER_BOX =
  'mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-edge bg-surface/80';
const ICON_WRAPPER_PLAIN = 'inline-flex shrink-0 items-center justify-center';
const VALUE_WRAPPER_BOX =
  'rounded-md border border-edge bg-surface px-2 py-0.5 text-xs font-semibold tabular-nums text-content';
const VALUE_WRAPPER_PLAIN = 'font-semibold tabular-nums text-content';

/**
 * Compact status / navigation row (counts, risk, queues). Renders a native
 * `<button>` when `onClick` is provided for accessibility.
 *
 * @param {string} props.label
 * @param {import('react').ReactNode} [props.value]
 * @param {import('lucide-react').LucideIcon} [props.icon]
 * @param {'neutral'|'blue'|'purple'|'green'|'amber'|'red'} [props.variant='neutral']
 * @param {string} [props.actionLabel]  Trailing text (e.g. “Open queue”).
 * @param {function} [props.onClick]
 * @param {import('react').ReactNode} [props.rightContent]  Overrides default value/action slot when set.
 * @param {boolean} [props.plain=false]  Omits bordered tiles on icon and value.
 * @param {string} [props.iconClassName='text-content-secondary']  Tailwind classes for the icon.
 * @param {string} [props.labelClassName]  Tailwind classes for the label.
 * @param {string} [props.valueClassName]  Tailwind classes for the value.
 * @param {string} [props.actionLabelClassName]  Tailwind classes for the action label.
 * @param {'start'|'center'|'end'} [props.labelAlign='start']  Vertical alignment of label vs icon.
 * @param {boolean} [props.showDot=false]  Renders an 8px dot before the label.
 * @param {string} [props.dotClassName]  Tailwind classes for the dot (e.g. `bg-amber-400`).
 * @param {string} [props.className]
 */
const DrawerStatusRow = ({
  label,
  value,
  icon: Icon,
  variant = 'neutral',
  actionLabel,
  onClick,
  rightContent,
  plain = false,
  iconClassName = 'text-content-secondary',
  labelClassName = '',
  valueClassName = '',
  actionLabelClassName = '',
  labelAlign = 'start',
  showDot = false,
  dotClassName = '',
  className = '',
}) => {
  const box = VARIANT_STYLES[variant] ?? VARIANT_STYLES.neutral;
  const interactive = typeof onClick === 'function';
  const labelRowAlign = LABEL_ALIGN_CLASSES[labelAlign] ?? LABEL_ALIGN_CLASSES.start;
  const iconWrapperClass = plain ? ICON_WRAPPER_PLAIN : ICON_WRAPPER_BOX;
  const iconClass = ['size-4', iconClassName].filter(Boolean).join(' ');
  const dotClass = ['size-2 shrink-0 rounded-full', dotClassName].filter(Boolean).join(' ');
  const labelClass = ['text-sm font-medium text-content', labelClassName].filter(Boolean).join(' ');
  const valueClass = [
    plain ? VALUE_WRAPPER_PLAIN : VALUE_WRAPPER_BOX,
    plain && !valueClassName ? 'text-xs' : '',
    valueClassName,
  ]
    .filter(Boolean)
    .join(' ');
  const actionLabelClass = ['text-xs font-medium text-primary', actionLabelClassName]
    .filter(Boolean)
    .join(' ');
  const ValueWrapper = plain ? 'span' : 'div';
  const hasValue = value != null && value !== '';

  const inner = (
    <>
      <div className={['flex min-w-0 flex-1 gap-3', labelRowAlign].join(' ')}>
        {Icon ? (
          <span className={iconWrapperClass}>
            <Icon className={iconClass} aria-hidden />
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            {showDot ? <span className={dotClass} aria-hidden /> : null}
            <p className={labelClass}>{label}</p>
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {rightContent != null ? (
          rightContent
        ) : (
          <>
            {hasValue ? <ValueWrapper className={valueClass}>{value}</ValueWrapper> : null}
            {actionLabel ? (
              <span className={actionLabelClass}>{actionLabel}</span>
            ) : null}
            {interactive ? <ChevronRight className="size-4 shrink-0 text-content-muted" aria-hidden /> : null}
          </>
        )}
      </div>
    </>
  );

  const baseClass = [
    'flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors',
    box,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (interactive) {
    return (
      <button type="button" className={baseClass} onClick={onClick}>
        {inner}
      </button>
    );
  }

  return <div className={baseClass}>{inner}</div>;
};

DrawerStatusRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  icon: PropTypes.elementType,
  variant: PropTypes.oneOf(['neutral', 'blue', 'purple', 'green', 'amber', 'red']),
  actionLabel: PropTypes.string,
  onClick: PropTypes.func,
  rightContent: PropTypes.node,
  plain: PropTypes.bool,
  iconClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  valueClassName: PropTypes.string,
  actionLabelClassName: PropTypes.string,
  labelAlign: PropTypes.oneOf(['start', 'center', 'end']),
  showDot: PropTypes.bool,
  dotClassName: PropTypes.string,
  className: PropTypes.string,
};

export default memo(DrawerStatusRow);
